import React, { useState } from "react";
import axios from "axios";
import { API_BASE } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Copy, Check, Download, ExternalLink, Loader2 } from "lucide-react";

const AS24_URL = "https://www.autoscout24.be/fr/dealer/vendre-voiture/#/";

const FOOTER = {
  nl: `– Bezoek onze website voor meer foto's in hoge resolutie, informatie en de Car-Pass: www.tr-premiumcars.be
– Wij doen ons best om onze advertenties zo nauwkeurig mogelijk te beschrijven, mocht er toch ergens een fout instaan zijn wij hier niet voor verantwoordelijk
– Standaard zit er telkens 1 jaar wettelijke waarborg inbegrepen in de prijs, in additie kan er ook een verlengde waarborg worden voorgelegd. Contacteer ons voor meer informatie.
* We speak English* *Wij spreken Nederlands* *Nous parlons Français*
Geef altijd een seintje voor bezoek, wij werken altijd op afspraak (mail, bellen, sms of WhatsApp)
Bij ons kunt u uw huidige wagen inruilen!`,
  fr: `– Visitez notre site web pour plus de photos en haute résolution, des informations et le Car-Pass : www.tr-premiumcars.be
– Nous faisons de notre mieux pour décrire nos annonces avec la plus grande précision ; en cas d'erreur éventuelle, nous ne pouvons en être tenus responsables.
– Une garantie légale d'un an est toujours incluse dans le prix. Une garantie prolongée peut en outre être proposée. Contactez-nous pour plus d'informations.
* We speak English* *Wij spreken Nederlands* *Nous parlons Français*
Prévenez-nous toujours avant votre visite : nous travaillons uniquement sur rendez-vous (e-mail, téléphone, SMS ou WhatsApp).
Chez nous, vous pouvez reprendre votre véhicule actuel !`,
};

// Website text (blank lines + "- " bullets) → compact AutoScout24 layout
const toPlain = (text) =>
  (text || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l.trim().replace(/^[-•·*]\s+/, ""))
    .filter(Boolean)
    .join("\n");

const frTitle = (title) => (title || "").replace(/\bpk\b/gi, "ch");

export const buildAs24Text = (form) =>
  [
    "🇳🇱",
    form.title,
    toPlain(form.description),
    FOOTER.nl,
    "🇫🇷",
    frTitle(form.title),
    toPlain(form.description_fr),
    FOOTER.fr,
  ]
    .filter(Boolean)
    .join("\n");

function CopyRow({ label, value, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value || "");
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0">
      <div className="w-20 sm:w-32 shrink-0 text-[10px] tracking-widest uppercase text-white/40 pt-1">{label}</div>
      <div className="flex-1 text-sm text-white/80 whitespace-pre-line line-clamp-3 min-w-0 break-words">{value || <span className="text-white/30">—</span>}</div>
      <button type="button" onClick={copy} disabled={!value} data-testid={`as24-copy-${testid}`}
        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-white/10 text-xs text-white/70 hover:text-[#c5a76f] hover:border-[#c5a76f]/40 disabled:opacity-30 transition-colors">
        {done ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} <span className="hidden sm:inline">{done ? "Gekopieerd" : "Kopieer"}</span>
      </button>
    </div>
  );
}

export function As24Helper({ form }) {
  const { toast } = useToast();
  const [zipping, setZipping] = useState(false);
  const images = (form.images || []).filter((u) => String(u).startsWith("/api/uploads/"));
  const adText = form.description ? buildAs24Text(form) : "";

  const downloadZip = async () => {
    setZipping(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/photos-zip`, { images, name: form.title || "wagen" }, { withCredentials: true, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${(form.title || "wagen").replace(/[^\w-]+/g, "_")}-fotos.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast({ title: "Downloaden mislukt" });
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="bg-[#242424] border border-white/5 rounded-lg p-5" data-testid="as24-helper">
      <div className="mb-3">
        <div className="text-white font-semibold">Plaatsen op AutoScout24</div>
        <div className="text-xs text-white/45">Kopieer de gegevens en plak ze in het formulier van AutoScout24. De advertentietekst bevat de Nederlandse én Franse versie.</div>
      </div>
      {form.description && !form.description_fr && (
        <div className="text-[11px] text-amber-400/80 mb-2" data-testid="as24-fr-missing">De Franse versie wordt gemaakt bij het opslaan — sla de wagen eerst op.</div>
      )}
      <CopyRow label="Chassisnummer" value={form.chassis} testid="vin" />
      <CopyRow label="Titel" value={form.title} testid="title" />
      <CopyRow label="Prijs" value={form.price !== "" && form.price != null ? String(form.price) : ""} testid="price" />
      <CopyRow label="Kilometerstand" value={form.km !== "" && form.km != null ? String(form.km) : ""} testid="km" />
      <CopyRow label="Advertentietekst NL + FR" value={adText} testid="description" />
      <div className="flex flex-wrap gap-2 mt-4">
        <button type="button" onClick={downloadZip} disabled={!images.length || zipping} data-testid="as24-download-photos"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded border border-white/10 text-white/80 hover:border-[#c5a76f]/40 hover:text-[#c5a76f] text-xs font-semibold uppercase tracking-wider disabled:opacity-40 transition-colors">
          {zipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Foto's downloaden ({images.length})
        </button>
        <a href={AS24_URL} target="_blank" rel="noreferrer" data-testid="as24-open-link"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded bg-[#c5a76f] text-black hover:bg-[#d8bd86] text-xs font-semibold uppercase tracking-wider transition-colors">
          <ExternalLink className="w-4 h-4" /> Open AutoScout24
        </a>
      </div>
    </div>
  );
}
